import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import type { TargetSubjectView } from "../api/types.js";

/**
 * Gate 1 "Choose subject" step (2026-09-25): the frame the user chose shows 2+ people, so before Gate 1 the user says which person is the
 * identity-replacement target and confirms it.
 *
 * 1. "choose": the FULL chosen frame with a clear box (numbered) around every detected person; clicking one selects it — the state is shown as
 *    `Selected subject` — and asks the server to render the marker image.
 * 2. "confirm": the marker image (the same full frame with a prominent frame around the selected person). "Use this person" confirms (handed to
 *    the caller, which owns the long Gate 1 request, exactly like the frame picker's "Use this frame"); "Choose a different person" goes back.
 *
 * Nothing here calls Phota or any provider; choosing a person only renders an image.
 */
export function SubjectSelector({
  tenantId,
  artifactId,
  onClose,
  onConfirm,
}: {
  readonly tenantId: string;
  readonly artifactId: string;
  readonly onClose: () => void;
  /** Called with the frame this choice was made on (`frameIndex` + `sha256`, verbatim from the server) the instant the user presses "Use this person". */
  readonly onConfirm: (frame: { readonly frameIndex: number; readonly sha256: string }) => void;
}) {
  const { csrfToken } = useAuth();
  const [view, setView] = useState<TargetSubjectView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<"choose" | "confirm">("choose");
  const [markerVersion, setMarkerVersion] = useState(0);
  const base = `/videos/${tenantId}/persona-source-stills/${artifactId}`;

  useEffect(() => {
    let cancelled = false;
    api
      .get<TargetSubjectView>(`${base}/target-subject`)
      .then((result) => {
        if (cancelled) return;
        setView(result);
        // A person chosen earlier (the window was closed after choosing) resumes at the confirmation step.
        if (result.selectedPersonId !== null && result.markerReady) setStage("confirm");
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Failed to load the people detected in this frame.");
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  async function choosePerson(personId: string): Promise<void> {
    if (view === null || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`${base}/target-subject/select`, csrfToken, { frameIndex: view.frameIndex, sha256: view.frameSha256, personId });
      setView({ ...view, selectedPersonId: personId, markerReady: true, confirmed: false });
      setMarkerVersion((version) => version + 1);
      setStage("confirm");
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : "Choosing this person failed — please try again.");
    } finally {
      setBusy(false);
    }
  }

  function usePerson(): void {
    if (view === null || view.selectedPersonId === null) return;
    onConfirm({ frameIndex: view.frameIndex, sha256: view.frameSha256 });
    onClose();
  }

  const selectedNumber = view === null || view.selectedPersonId === null ? null : view.people.findIndex((person) => person.id === view.selectedPersonId) + 1;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Choose subject">
      <div className="modal-content card" style={{ maxWidth: 720 }}>
        <div className="progress-header">
          <span className="progress-stage-label">Choose subject</span>
          <button onClick={onClose}>Close</button>
        </div>
        {loadError && <div className="error-banner">{loadError}</div>}
        {actionError && <div className="error-banner">{actionError}</div>}

        {view === null ? (
          !loadError && <p className="muted">Loading the people in this frame…</p>
        ) : stage === "choose" ? (
          <>
            <p className="muted">This frame shows more than one person. Click the person Gate 1 should replace — everyone else stays exactly as they are.</p>
            <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", background: "#000" }}>
              <img
                className="preview-image"
                style={{ display: "block", maxWidth: "100%", maxHeight: 420 }}
                src={`${base}/source-frame-candidates/${view.frameIndex}/preview`}
                alt="The frame you chose"
                data-testid="subject-frame"
              />
              {view.people.map((person, index) => (
                <button
                  key={person.id}
                  type="button"
                  disabled={busy}
                  onClick={() => void choosePerson(person.id)}
                  aria-label={`Person ${index + 1}`}
                  aria-pressed={person.id === view.selectedPersonId}
                  data-testid={`subject-person-${person.id}`}
                  style={{
                    position: "absolute",
                    left: `${person.box.x * 100}%`,
                    top: `${person.box.y * 100}%`,
                    width: `${person.box.width * 100}%`,
                    height: `${person.box.height * 100}%`,
                    border: person.id === view.selectedPersonId ? "4px solid #22c55e" : "3px solid #ef4444",
                    background: person.id === view.selectedPersonId ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.08)",
                    cursor: "pointer",
                    padding: 0,
                    color: "#fff",
                    fontWeight: 700,
                    textShadow: "0 0 4px #000",
                    textAlign: "left",
                    verticalAlign: "top",
                  }}
                >
                  <span style={{ background: "#000", padding: "1px 6px" }}>{index + 1}</span>
                </button>
              ))}
            </div>
            <p className="muted" data-testid="subject-selection-state" style={{ marginTop: 8 }}>
              {selectedNumber === null ? "No subject selected yet." : `Selected subject: person ${selectedNumber}`}
            </p>
          </>
        ) : (
          <>
            <p data-testid="subject-selection-state">
              <strong>Selected subject: person {selectedNumber}</strong>
            </p>
            <p className="muted">The frame around the chosen person is only a marker for Gate 1 — the original frame is not changed. Is this the right person?</p>
            <img
              className="preview-image"
              style={{ display: "block", maxWidth: "100%", maxHeight: 420, background: "#000" }}
              src={`${base}/target-subject/marker?v=${markerVersion}`}
              alt="The frame with the chosen person outlined"
              data-testid="subject-marker"
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="success" disabled={busy} onClick={usePerson}>
                Use this person
              </button>
              <button disabled={busy} onClick={() => setStage("choose")}>
                Choose a different person
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
